import React, {useEffect, useState} from 'react';
import {useHistory, withRouter} from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import {scheduleMatch} from "../../services/matches";
import {NotificationManager} from "react-notifications";
import MySwal from "sweetalert2";
import moment from 'moment'
import {isWithinAbsencePeriod, setSendDateUTC} from "../../utils";
import {useTranslation} from "react-i18next";
import {useAuth} from "../../contexts/authContext";

// React Components
import DialogCard from '../DialogCard';
import Modal from '../Modal';
import InputSelect from "../InputSelect";
import DateTimePicker from "../DateTimePicker";

// CSS Files
import './styles.css';

const ScheduleDialog = (props) => {
   const history = useHistory();
   const [date, setDate] = useState(new Date(props.content.matchOriginalSchedule));
   const [club, setClub] = useState('');
   const [field, setField] = useState('');
   const [absenceDates, setAbsenceDates] = useState([]);
   const [consent, setConsent] = useState(false);
   const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
   const { user } = useAuth();
   const { t } = useTranslation();
   const absencePeriods = (props.content.absencePeriods || []).filter(period =>
      isValidDate(period.startDate) && isValidDate(period.endDate) && period.startDate <= period.endDate
   );
   const opponentAbsencePeriods = absencePeriods.filter(period => period.source === "opponent");
   const scheduleStartDate = new Date(props.content?.leagueEditionStartDate);

   useEffect(() => {
      let allowedDate = getFirstAllowedDate(date);

      if(allowedDate.getTime() !== date.getTime()) {
         setDate(allowedDate);
      }
      setAbsenceDates(buildAbsenceDates(absencePeriods));
   }, []);

   function isValidDate(date) {
      return date instanceof Date && !isNaN(date.getTime());
   }

   function getFirstAllowedDate(selectedDate) {
      let allowedDate = isValidDate(selectedDate) ? new Date(selectedDate) : new Date(scheduleStartDate);
      const selectedHours = allowedDate.getHours();
      const selectedMinutes = allowedDate.getMinutes();

      let blockingPeriod = absencePeriods.find(period => isWithinAbsencePeriod(allowedDate, period.startDate, period.endDate));

      while (blockingPeriod) {
         allowedDate = new Date(blockingPeriod.endDate);
         allowedDate.setDate(allowedDate.getDate() + 1);
         allowedDate.setHours(selectedHours, selectedMinutes);

         blockingPeriod = absencePeriods.find(period => isWithinAbsencePeriod(allowedDate, period.startDate, period.endDate));
      }

      return allowedDate;
   }

   const buildAbsenceDates = periods => {
      let absenceDates = [];

      periods.forEach(period => {
         for (let date = new Date(period.startDate); date <= period.endDate; date.setDate(date.getDate() + 1)) {
            absenceDates.push(new Date(date));
         }
      });

      return absenceDates;
   }

   const translateAbsenceReason = reason => {
      const absenceReasonTranslations = {
         "Lesão": t("notify_absence_page.absence_reasons.injury"),
         "Trabalho/férias": t("notify_absence_page.absence_reasons.work_vacation"),
         "Procuro substituta/o": t("notify_absence_page.absence_reasons.looking_for_sub"),
         "Queremos desistir": t("notify_absence_page.absence_reasons.want_to_quit")
      };

      return absenceReasonTranslations[reason] || reason;
   }

   const formatAbsenceNotice = period => {
      const dates = t("to_schedule_page.prevented_to_from", { interpolation: { escapeValue: false }, from: moment(period.startDate).format('DD/MM/YYYY'), to: moment(period.endDate).format('DD/MM/YYYY') });

      return t("to_schedule_page.scheduling_prevented_for_dates", { interpolation: { escapeValue: false }, dates, reason: translateAbsenceReason(period.reason) });
   }

   const validateSchedule = () => {
      let isAcceptable = true;

      if (date === '') {
         isAcceptable = false;
         NotificationManager.error(t("scheduled_page.errors.mandatory_date"), t("common.error"), 4000);
      } else if (club === '') {
         isAcceptable = false;
         NotificationManager.error(t("scheduled_page.errors.mandatory_club"), t("common.error"), 4000);
      } else if (consent === false) {
         isAcceptable = false;
         NotificationManager.error(t("scheduled_page.errors.mandatory_consent"), t("common.error"), 4000);
      }

      return isAcceptable;
   }

   const schedule = () => {
      let scheduleDate = moment(date);
      let scheduleDay = scheduleDate.format(`${t("common.date_formatting.day")}`);
      let scheduleMonth = scheduleDate.format("MMMM");

      MySwal.fire({
         titleText: t("scheduled_page.scheduling"),
         text: t("to_schedule_page.schedule_confirmation", {hour: scheduleDate.format("HH[h]mm"), day: scheduleDay, month: scheduleMonth}),
         showCancelButton: true,
         cancelButtonText: t("common.cancel"),
         confirmButtonText: t("common.yes"),
         allowOutsideClick: false,
         allowEscapeKey: false,
         preConfirm: async () => {
            try {
               return await scheduleMatch(props.content?.matchId, club, setSendDateUTC(date));
            } catch (e) {
               NotificationManager.error(t("common.form.errors.general"), t("common.error"), 4000);
               return false;
            }
         }
      }).then(async (res) => {
         if (res.dismiss === MySwal.DismissReason.cancel) {
            MySwal.close();
         } else {
            MySwal.fire({
               text: t("to_schedule_page.success"),
               allowOutsideClick: false
            }).then(() => {
               history.push("/my-games");
            });
         }
      })
   }

   return <DialogCard title={t("to_schedule_page.scheduling")}>
      <div className="schedule-form-wrapper">
         {opponentAbsencePeriods.map((period, index) => (
            <div key={`absence-period-${index}`} className="alert alert-info mb-2" role="alert">
               {formatAbsenceNotice(period)}
            </div>
         ))}
         <Button onClick={() => setShowAvailabilityModal(true)} style={{backgroundColor: "#F2F2F2 !important"}} className="w-100" variant={"secondary"}>{t("to_schedule_page.check_field_availability")}</Button>
         <div className="form-group mt-2">
            <DateTimePicker
               datePickerLabel={t("common.date")}
               dateFormat={`dd/MM/yyyy ${t("to_schedule_page.at")} HH:mm`}
               minDate={props.content?.leagueEditionStartDate}
               maxDate={props.content?.leagueEditionEndDate}
               isScheduled={props.content?.isScheduled}
               excludedDates={absenceDates}
               showTimeSelect={true}
               date={date}
               onChange={(date) => setDate(date)}
            />
         </div>
         <div className="form-group mt-4">
            <InputSelect text={t("to_schedule_page.club_field")}>
               <select className="form-control mb-2" value={club} onChange={(e) => setClub(e.target.value)}>
                  <option value="" disabled>{t("to_schedule_page.choose_club")}</option>
                  {props.content.clubs?.map((club) => <option key={club.id} value={club.id}>{club.name}</option>)}
               </select>
            </InputSelect>
            <input type="text" className="form-control" placeholder={t("to_schedule_page.field_id")} onChange={(e) => setField(e.target.value)}/>
         </div>
         <div className="form-check mt-4" style={{paddingLeft: 25}}>
            <input type="checkbox" className="form-check-input" id="check-resp" checked={consent} onChange={(e) => setConsent(!consent)} style={{width: 20, height: 20, marginLeft: -25}}/>
            <label className="form-check-label" htmlFor="check-resp">{t("to_schedule_page.consent")}</label>
         </div>
         <Button
            className="w-100 btn-thick mt-4 mb-2"
            variant={"primary"}
            onClick={async () => {
               if (validateSchedule()) {
                  schedule();
               }
            }}
         >
            {t("to_schedule_page.schedule")}
         </Button>
         <Button onClick={props.close} className="w-100 btn-thick" variant={"secondary"}>{t("common.close")}</Button>
      </div>
      <Modal show={showAvailabilityModal}
             onHide={() => setShowAvailabilityModal(false)}>
         <div className="availability-modal-container">
            <ButtonGroup vertical className="w-100">
               <div className="btn btn-secondary mb-2">
                  <span style={{fontSize: 12}}>{t("scheduled_page.platforms_alert")}</span>
               </div>
               <a href={"https://playtomic.io/"} target={"_blank"} className="w-100 field-link">Playtomic</a>
               <a href={"https://www.tiesports.com/"} target={"_blank"} className="w-100 field-link">TiePadel</a>
               <Button onClick={() => {
                  history.push(`/my-games/game/${props.content?.matchId}/participating_clubs`, {clubs: props.content.clubs})
               }} className="w-100 btn-thick" variant={"secondary"}>{t("scheduled_page.partner_clubs")}</Button>
            </ButtonGroup>
            <Button onClick={() => setShowAvailabilityModal(false)} className="w-100 mt-2 btn-thick" variant={"secondary"}>{t("common.cancel")}</Button>
         </div>
      </Modal>
   </DialogCard>
};

export default withRouter(ScheduleDialog);
